import {useAccessToken, useIsBankUser, useIsUnitUser} from "../../services/auth"
import {AllowedProduct, Org, updateOrg} from "../../resources/org"
import React, {useEffect, useState} from "react"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import {EditMode, ErrorDocument, RiskRate} from "../../resources/common"
import {useToasts} from "react-toast-notifications"
import {OrgAttributes} from "./OrgAttributes"
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome"
import {faEdit, faWindowClose} from "@fortawesome/free-solid-svg-icons"
import OrgAccountsSummary from "../../components/OrgAccountsSummary/OrgAccountsSummary"
import OrgAndCustomerAccountsSummary from "../../components/OrgAccountsSummary/OrgAndCustomerAccountsSummary"
import InterestAndDepositFeeSummary from "../../components/InterestAndDepositFeeSummary/InterestAndDepositFeeSummary"
import EndCustomerDepositSummary from "../../components/EndCustomerDepositSummary/EndCustomerDepositSummary"
import {Customer} from "../../resources/customer"
import {AsyncResultIdleRequestState} from "../../types/asyncResultIdle"

interface OrgOverviewProps {
	org: Org
	currentDefaultBankName?: string
	customers: Customer[]
	bankNames?: string[]
	refresh: () => void
}
export default function OrgOverview({org, currentDefaultBankName, customers, bankNames, refresh}: OrgOverviewProps) {
	const accessToken = useAccessToken()
	const {addToast} = useToasts()

	const [state, update] = useAsyncResultIdle(updateOrg)
	const [customerId, setCustomerId] = useState<string | null>(
		org.attributes.customerId ? org.attributes.customerId.toString() : ""
	)
	const [verificationServiceId, setVerificationServiceId] = useState<string>(
		org.attributes.verificationServiceId ? org.attributes.verificationServiceId : ""
	)
	const [supportWidgetId, setSupportWidgetId] = useState<string | undefined | null>(org.attributes.supportWidgetId)
	const [orgInterchangeShare, setOrgInterchangeShare] = useState<number>(org.attributes.orgInterchangeShare)
	const [defaultCustomerRiskRate, setDefaultCustomerRiskRate] = useState<RiskRate>(
		org.attributes.defaultCustomerRiskRate
	)

	const [selectedDefaultBankName, setSelectedDefaultBankName] = useState<string | undefined>(currentDefaultBankName)

	const [enableTwoFactorAuthentication, setEnableTwoFactorAuthentication] = useState<boolean>(
		org.attributes.enableTwoFactorAuthentication
	)
	const [isEditing, setIsEditing] = useState(false)

	const [name, setName] = useState(org.attributes.name)

	const [allowedProducts, setAllowedProducts] = useState<AllowedProduct[]>(org.attributes.allowedProducts ?? [])

	useEffect(() => {
		if (state.isOk()) {
			addToast("Org Updated Successfully", {appearance: "success"})
			setIsEditing(false)
			refresh()
		}
	}, [state])

	const submit = () => {
		update(
			accessToken,
			org.id,
			customerId != "" ? customerId : null,
			defaultCustomerRiskRate,
			verificationServiceId != "" ? verificationServiceId : undefined,
			name,
			enableTwoFactorAuthentication,
			selectedDefaultBankName !== currentDefaultBankName ? selectedDefaultBankName : undefined,
			supportWidgetId,
			allowedProducts
		)
	}

	const disabled = state.match(
		() => false,
		() => true,
		(_) => true,
		(_) => false
	)
	const isBankUser = useIsBankUser()
	const isUnitUser = useIsUnitUser()
	return (
		<>
			<div className="columns">
				<div className="column">
					<div className="buttons">
						{!isBankUser && (
							<button className="button is-light" onClick={() => (!isEditing ? setIsEditing(true) : refresh())}>
								<span>{isEditing ? "Cancel" : "Edit"}</span>
								<span className="icon">
									<FontAwesomeIcon icon={isEditing ? faWindowClose : faEdit} />
								</span>
							</button>
						)}
					</div>
				</div>
			</div>
			{!isBankUser && (
				<div className="columns">
					<div className="column">
						<form
							onSubmit={(e) => {
								e.preventDefault()
								submit()
							}}
						>
							<fieldset disabled={disabled}>
								<OrgAttributes
									editMode={isEditing ? EditMode.EDIT : EditMode.VIEW}
									name={name}
									setName={setName}
									customerId={customerId}
									setCustomerId={setCustomerId}
									customers={customers}
									bankNames={bankNames}
									defaultBankName={selectedDefaultBankName}
									setDefaultBankName={setSelectedDefaultBankName}
									setOrgInterchangeShare={setOrgInterchangeShare}
									orgInterchangeShare={orgInterchangeShare}
									defaultCustomerRiskRate={defaultCustomerRiskRate}
									setDefaultCustomerRiskRate={setDefaultCustomerRiskRate}
									twilioVerificationServiceId={verificationServiceId}
									setTwilioVerificationServiceId={setVerificationServiceId}
									enableTwoFactorAuthentication={enableTwoFactorAuthentication}
									setEnableTwoFactorAuthentication={setEnableTwoFactorAuthentication}
									supportWidgetId={supportWidgetId}
									setSupportWidgetId={setSupportWidgetId}
									allowedProducts={allowedProducts}
									setAllowedProducts={setAllowedProducts}
								/>

								{isEditing ? (
									<div className="columns">
										<div className="column">
											<div className="card">
												<Footer state={state} />
											</div>
										</div>
									</div>
								) : null}
							</fieldset>
						</form>
					</div>
				</div>
			)}
			<div className="columns">
				<div className="column">
					{org.attributes.customerId ? (
						<OrgAndCustomerAccountsSummary
							orgId={org.id}
							customerId={org.attributes.customerId.toString()}
							customerAccountsAccessToken={accessToken}
							showIcon
						/>
					) : (
						<OrgAccountsSummary orgId={org.id} accessToken={accessToken} showIcon />
					)}
				</div>
			</div>
			{isBankUser && (
				<div className="columns">
					<div className="column is-flex is-flex-direction-column">
						<EndCustomerDepositSummary orgId={org.id} accessToken={accessToken} />
					</div>
					<div className="column">
						<InterestAndDepositFeeSummary orgId={org.id} accessToken={accessToken} />
					</div>
				</div>
			)}
			{isUnitUser && (
				<div className="columns">
					<div className="column is-flex is-flex-direction-column">
						<EndCustomerDepositSummary orgId={org.id} accessToken={accessToken} />
					</div>
				</div>
			)}
		</>
	)
}

function Footer({state}: {state: AsyncResultIdleRequestState<Org, ErrorDocument>}) {
	return (
		<div className="card-content">
			{state.match(
				() => (
					<button className="button is-success">Update</button>
				),
				() => (
					<button className="button is-success is-loading">Updating</button>
				),
				(_) => null,
				(err) => (
					<>
						<div className="has-text-danger">{err.errors[0].title}</div>
						<button className="button is-success">Create</button>
					</>
				)
			)}
		</div>
	)
}
